import { Suspense } from 'react'
import { useSelector } from 'react-redux'
import AppErrorBoundary from '@/components/common/AppErrorBoundary'
import { useSocketConnected } from '@/hooks/socketStatus'
import { useHasOpened } from '@/hooks/useHasOpened'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

// The panel (and the chat cards' chart code) loads the first time it opens.
const AiChatPanel = lazyWithRetry(() => import('./AiChatPanel'), 'components/ai-chat/AiChatPanel')

/**
 * Where a layout mounts the assistant panel: nothing until it first opens,
 * then mounted for good so closing keeps its exit animation. Its own error
 * boundary: a panel chunk missing after a deploy (or a panel error) must not
 * take the page down with it. The socket's state sets how often it polls.
 * Mount it inside the layout's `PageContextProvider`.
 *
 * @param {Object} props
 * @param {number} [props.reservedWidth] - px the layout keeps beside the page besides the panel
 *   (the app sidebar): with it the panel docks only where the page keeps enough width (`panelLayout.js`)
 * @returns {JSX.Element|null}
 */
export default function AssistantPanelMount({ reservedWidth = 0 }) {
  const isOpen = useSelector((state) => Boolean(state.aiChat?.panelOpen))
  const hasOpened = useHasOpened(isOpen)
  const isConnected = useSocketConnected()
  if (!hasOpened) return null
  return (
    <AppErrorBoundary name="assistant" floating>
      <Suspense fallback={null}>
        <AiChatPanel socketConnected={isConnected} reservedWidth={reservedWidth} />
      </Suspense>
    </AppErrorBoundary>
  )
}
